// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// Tree layout helpers.
//
// applyTreeLayout mutates node.bx / node.by (and for circular: bcx, bcy),
// sets node.angle, node.angleDegrees, node.inverted, then centres the whole
// tree so its centroid sits at the origin.
//
// centreNodes is also exported so consumers can call it independently.

import { Angles, TreeTypes } from "./constants.js";

function calculateNodeBounds(nodes, firstIndex, lastIndex, xAxis, yAxis) {
  let minX = Number.MAX_SAFE_INTEGER;
  let minY = Number.MAX_SAFE_INTEGER;
  let maxX = Number.MIN_SAFE_INTEGER;
  let maxY = Number.MIN_SAFE_INTEGER;

  for (let i = firstIndex; i <= lastIndex; i++) {
    const node = nodes[i];
    if (node[xAxis] < minX) minX = node[xAxis];
    if (node[yAxis] < minY) minY = node[yAxis];
    if (node[xAxis] > maxX) maxX = node[xAxis];
    if (node[yAxis] > maxY) maxY = node[yAxis];
  }

  return { min: [minX, minY], max: [maxX, maxY] };
}

export function centreNodes(graph, firstIndex, lastIndex, isCircular, centreRoot) {
  let minX, minY, maxX, maxY;

  if (centreRoot) {
    // Radial/circular centred layout: bounds derived from total subtree length
    minX = -graph.root.totalSubtreeLength;
    minY = -graph.root.totalSubtreeLength;
    maxX = graph.root.totalSubtreeLength;
    maxY = graph.root.totalSubtreeLength;
  } else {
    const bounds = calculateNodeBounds(
      graph.postorderTraversal,
      firstIndex,
      lastIndex,
      "bx",
      "by",
    );
    minX = bounds.min[0];
    minY = bounds.min[1];
    maxX = bounds.max[0];
    maxY = bounds.max[1];
  }

  const dx = (maxX + minX) / 2;
  const dy = (maxY + minY) / 2;

  for (let i = firstIndex; i <= lastIndex; i++) {
    const node = graph.postorderTraversal[i];
    node.bx -= dx;
    node.by -= dy;
    if (isCircular) {
      node.bcx -= dx;
      node.bcy -= dy;
    }
    node.angleDegrees = 360 - ((node.angle / Angles.Degrees360) * 360);
  }

  return {
    min: [minX - dx, minY - dy],
    max: [maxX - dx, maxY - dy],
  };
}

export function applyTreeLayout(graph, treeType, alignableLabels) {
  const firstPostIndex = graph.root.postIndex - graph.root.totalNodes + 1;
  const lastPostIndex = graph.root.postIndex;

  if (treeType === TreeTypes.Circular) {
    let stepOffset = 0;

    for (let i = firstPostIndex; i < lastPostIndex; i++) {
      const node = graph.postorderTraversal[i];

      if (node.isLeaf) {
        node.angle = stepOffset * (Angles.Degrees360 / graph.root.visibleLeaves);
      } else {
        const startAngle = node.children[0].angle;
        const endAngle = node.children[node.children.length - 1].angle;
        node.angle = (endAngle + startAngle) / 2;
      }

      node.coangle = node.angle + Angles.Degrees90;

      const xComponent = Math.cos(node.angle);
      const yComponent = Math.sin(node.angle);

      node.bx = node.distanceFromRoot * xComponent;
      node.by = node.distanceFromRoot * yComponent;
      node.bcx = node.parent.distanceFromRoot * xComponent;
      node.bcy = node.parent.distanceFromRoot * yComponent;
      node.inverted =
        node.angle > Angles.Degrees90 && node.angle <= Angles.Degrees270;

      if (!node.isHidden && (node.isLeaf || node.isCollapsed)) {
        stepOffset += 1;
      }
    }

    // Root node sits at origin
    graph.root.bx = 0;
    graph.root.by = 0;
    graph.root.inverted =
      graph.root.angle > Angles.Degrees90 &&
      graph.root.angle <= Angles.Degrees270;

    return centreNodes(graph, firstPostIndex, lastPostIndex, true, alignableLabels);
  }

  if (treeType === TreeTypes.Diagonal) {
    let stepOffset = 0;

    for (let i = firstPostIndex; i <= lastPostIndex; i++) {
      const node = graph.postorderTraversal[i];
      node.angle = Angles.Degrees0;
      node.inverted = false;

      node.bx = (graph.root.visibleLeaves - node.visibleLeaves) / 2;

      if (node.isLeaf) {
        node.by = stepOffset;
      } else if (node.isCollapsed) {
        node.by = stepOffset;
      } else {
        const subTreeHeight = node.visibleLeaves - node.children[0].visibleLeaves;
        node.by = node.children[0].by + subTreeHeight / 2;
      }

      if (node.isLeaf) {
        stepOffset += 1;
      }
    }

    return centreNodes(graph, firstPostIndex, lastPostIndex, false, false);
  }

  if (treeType === TreeTypes.Hierarchical) {
    let stepOffset = 0;

    for (let i = firstPostIndex; i <= lastPostIndex; i++) {
      const node = graph.postorderTraversal[i];
      node.angle = Angles.Degrees90;
      node.inverted = false;
      node.by = node.distanceFromRoot;

      if (node.isLeaf) {
        node.bx = -1 * stepOffset;
      } else if (node.isCollapsed) {
        node.bx = stepOffset;
      } else {
        const startX = node.children[0].bx;
        const endX = node.children[node.children.length - 1].bx;
        node.bx = (endX + startX) / 2;
      }

      if (!node.isHidden && (node.isLeaf || node.isCollapsed)) {
        stepOffset += 1;
      }
    }

    return centreNodes(graph, firstPostIndex, lastPostIndex, false, false);
  }

  if (treeType === TreeTypes.Radial) {
    let stepOffset = 0;

    for (let i = firstPostIndex; i <= lastPostIndex; i++) {
      const node = graph.postorderTraversal[i];

      if (node.isLeaf) {
        node.angle = stepOffset * (Angles.Degrees360 / graph.root.visibleLeaves);
      } else {
        let angle = 0;
        for (const child of node.children) {
          angle += child.angle * child.totalLeaves;
        }
        node.angle = angle / node.totalLeaves;
      }

      if (!node.isHidden && (node.isLeaf || node.isCollapsed)) {
        stepOffset += 1;
      }
    }

    graph.root.bx = graph.root.branchLength * Math.cos(graph.root.angle);
    graph.root.by = graph.root.branchLength * Math.sin(graph.root.angle);
    graph.root.inverted =
      graph.root.angle > Angles.Degrees90 &&
      graph.root.angle <= Angles.Degrees270;
    graph.root.quarter = Math.floor(graph.root.angle / Angles.Degrees90) + 1;

    for (let i = 1; i < graph.root.totalNodes; i++) {
      const node = graph.preorderTraversal[graph.root.preIndex + i];
      node.bx = node.parent.bx + node.branchLength * Math.cos(node.angle);
      node.by = node.parent.by + node.branchLength * Math.sin(node.angle);
      node.inverted =
        node.angle > Angles.Degrees90 && node.angle <= Angles.Degrees270;
      node.quarter = Math.floor(node.angle / Angles.Degrees90) + 1;
    }

    return centreNodes(graph, firstPostIndex, lastPostIndex, false, false);
  }

  if (treeType === TreeTypes.Rectangular) {
    let stepOffset = 0;

    for (let i = firstPostIndex; i <= lastPostIndex; i++) {
      const node = graph.postorderTraversal[i];
      node.angle = Angles.Degrees0;
      node.inverted = false;
      node.bx = node.distanceFromRoot;

      if (node.isLeaf) {
        node.by = stepOffset;
      } else if (node.isCollapsed) {
        node.by = stepOffset;
      } else {
        const startY = node.children[0].by;
        const endY = node.children[node.children.length - 1].by;
        node.by = (endY + startY) / 2;
      }

      if (!node.isHidden && (node.isLeaf || node.isCollapsed)) {
        stepOffset += 1;
      }
    }

    return centreNodes(graph, firstPostIndex, lastPostIndex, false, false);
  }

  throw new Error(`applyTreeLayout: unrecognised tree type "${treeType}"`);
}
